import React, { useState, useEffect } from 'react';
import {
  Key,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Zap,
  Globe,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useApp } from '../../context/AppContext';
import { aiSettingsService } from '../../services/firebaseService';
import './AISettings.css';

// Predefined AI Providers
const AI_PROVIDERS = [
  {
    id: 'openai',
    name: 'OpenAI (ChatGPT)',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
    baseUrl: 'https://api.openai.com/v1',
    testEndpoint: '/chat/completions',
    models: ['gpt-5', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo', 'o1', 'o1-mini', 'o1-pro', 'o3', 'o3-mini', 'o4-mini'],
    defaultModel: 'gpt-4o',
    docUrl: 'https://platform.openai.com/docs',
    keyPrefix: 'sk-',
    testPayload: (model) => ({
      model: model,
      messages: [{ role: 'user', content: 'Say "API Connected Successfully" in 5 words or less.' }],
      max_tokens: 20
    }),
    authHeader: (key) => ({ 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' })
  },
  {
    id: 'anthropic',
    name: 'Anthropic (Claude)',
    logo: 'https://www.anthropic.com/images/icons/apple-touch-icon.png',
    baseUrl: 'https://api.anthropic.com/v1',
    testEndpoint: '/messages',
    models: ['claude-sonnet-4-20250514', 'claude-opus-4-20250514', 'claude-3-7-sonnet-20250219', 'claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022', 'claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307'],
    defaultModel: 'claude-sonnet-4-20250514',
    docUrl: 'https://docs.anthropic.com',
    keyPrefix: 'sk-ant-',
    testPayload: (model) => ({
      model: model,
      max_tokens: 20,
      messages: [{ role: 'user', content: 'Say "API Connected Successfully" in 5 words or less.' }]
    }),
    authHeader: (key) => ({
      'x-api-key': key,
      'Content-Type': 'application/json',
      'anthropic-version': '2023-06-01'
    })
  },
  {
    id: 'huggingface',
    name: 'Hugging Face',
    logo: 'https://huggingface.co/front/assets/huggingface_logo-noborder.svg',
    baseUrl: 'https://api-inference.huggingface.co/models',
    testEndpoint: '',
    models: ['meta-llama/Meta-Llama-3-8B-Instruct', 'mistralai/Mixtral-8x7B-Instruct-v0.1', 'microsoft/Phi-3-mini-4k-instruct', 'google/gemma-7b-it'],
    defaultModel: 'microsoft/Phi-3-mini-4k-instruct',
    docUrl: 'https://huggingface.co/docs/api-inference',
    keyPrefix: 'hf_',
    testPayload: (model) => ({
      inputs: 'Say "API Connected Successfully" in 5 words or less.',
      parameters: { max_new_tokens: 20 }
    }),
    authHeader: (key) => ({ 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' })
  },
  {
    id: 'google',
    name: 'Google AI (Gemini)',
    logo: 'https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
    testEndpoint: '/models/{model}:generateContent',
    models: ['gemini-pro', 'gemini-1.5-pro', 'gemini-1.5-flash'],
    defaultModel: 'gemini-pro',
    docUrl: 'https://ai.google.dev/docs',
    keyPrefix: 'AIza',
    testPayload: (model) => ({
      contents: [{ parts: [{ text: 'Say "API Connected Successfully" in 5 words or less.' }] }]
    }),
    authHeader: (key) => ({ 'Content-Type': 'application/json' }),
    urlWithKey: true
  },
  {
    id: 'cohere',
    name: 'Cohere',
    logo: 'https://cohere.com/favicon.ico',
    baseUrl: 'https://api.cohere.ai/v1',
    testEndpoint: '/generate',
    models: ['command', 'command-light', 'command-r', 'command-r-plus'],
    defaultModel: 'command',
    docUrl: 'https://docs.cohere.com',
    keyPrefix: '',
    testPayload: (model) => ({
      model: model,
      prompt: 'Say "API Connected Successfully" in 5 words or less.',
      max_tokens: 20
    }),
    authHeader: (key) => ({ 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' })
  },
  {
    id: 'openrouter',
    name: 'OpenRouter',
    logo: 'https://openrouter.ai/favicon.ico',
    baseUrl: 'https://openrouter.ai/api/v1',
    testEndpoint: '/chat/completions',
    models: [
      // Free vision models (unlimited requests, support images)
      'nvidia/nemotron-nano-12b-v2-vl:free',
      'meta-llama/llama-3.2-11b-vision-instruct:free',
      'qwen/qwen-2.5-vl-7b-instruct:free',
      'qwen/qwen-2-vl-7b-instruct:free',
      'google/gemini-2.0-flash-exp:free',
      // Free text-only models
      'allenai/olmo-3.1-32b-think:free',
      'google/gemini-2.0-flash-thinking-exp:free',
      // Paid models
      'openai/gpt-4o',
      'openai/gpt-4o-mini',
      'openai/gpt-4-turbo',
      'anthropic/claude-3.5-sonnet',
      'anthropic/claude-3-haiku',
      'google/gemini-pro-1.5',
      'google/gemini-flash-1.5',
      'meta-llama/llama-3.1-70b-instruct',
      'meta-llama/llama-3.1-8b-instruct',
      'mistralai/mixtral-8x7b-instruct',
      'deepseek/deepseek-chat',
      'qwen/qwen-2-72b-instruct'
    ],
    defaultModel: 'nvidia/nemotron-nano-12b-v2-vl:free',
    docUrl: 'https://openrouter.ai/docs',
    keyPrefix: 'sk-or-',
    testPayload: (model) => ({
      model: model,
      messages: [{ role: 'user', content: 'Say "API Connected Successfully" in 5 words or less.' }],
      max_tokens: 20
    }),
    authHeader: (key) => ({
      'Authorization': `Bearer ${key}`,
      'Content-Type': 'application/json',
      'HTTP-Referer': window.location.origin,
      'X-Title': 'ANB Vision Admin'
    })
  },
  {
    id: 'custom',
    name: 'Custom API',
    logo: null,
    baseUrl: '',
    testEndpoint: '',
    models: [],
    defaultModel: '',
    docUrl: '',
    keyPrefix: '',
    isCustom: true
  }
];

function AISettings() {
  const { addNotification } = useApp();

  // State for API configurations
  const [apiConfigs, setApiConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [showApiKey, setShowApiKey] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Form state for new/edit
  const [formData, setFormData] = useState({
    providerId: '',
    name: '',
    apiKey: '',
    baseUrl: '',
    model: '',
    isActive: true,
    customHeaders: '',
    customEndpoint: ''
  });

  // Load configs from Firebase on mount
  useEffect(() => {
    setIsLoading(true);

    // Subscribe to real-time updates from Firebase
    const unsubscribe = aiSettingsService.subscribeToAll((configs) => {
      console.log('[AISettings] Loaded configs from Firebase:', configs.length);
      setApiConfigs(configs);

      // Find active config
      const active = configs.find(c => c.isActive);
      if (active) {
        setActiveConfig(active.id);
        // Also store in localStorage for other services to use
        localStorage.setItem('ai_active_config', active.id);
        localStorage.setItem('ai_active_config_full', JSON.stringify({
          ...active,
          provider: active.providerId,
        }));
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sync active config to localStorage when it changes (for other services)
  useEffect(() => {
    if (activeConfig && apiConfigs.length > 0) {
      const fullConfig = apiConfigs.find(c => c.id === activeConfig);
      if (fullConfig) {
        localStorage.setItem('ai_active_config', activeConfig);
        localStorage.setItem('ai_active_config_full', JSON.stringify({
          ...fullConfig,
          provider: fullConfig.providerId,
          isActive: true
        }));
      }
    }
  }, [activeConfig, apiConfigs]);

  // Get provider info
  const getProvider = (providerId) => {
    return AI_PROVIDERS.find(p => p.id === providerId) || AI_PROVIDERS.find(p => p.id === 'custom');
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-fill baseUrl and model when provider is selected
    if (field === 'providerId') {
      const provider = getProvider(value);
      if (provider && !provider.isCustom) {
        setFormData(prev => ({
          ...prev,
          providerId: value,
          name: provider.name,
          baseUrl: provider.baseUrl,
          model: provider.defaultModel
        }));
      }
    }
  };

  // Mask API key for display
  const maskApiKey = (key) => {
    if (!key) return '';
    if (key.length <= 8) return '*'.repeat(key.length);
    return key.substring(0, 4) + '*'.repeat(key.length - 8) + key.substring(key.length - 4);
  };

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      addNotification({ type: 'success', message: 'Copied to clipboard!' });
    } catch (err) {
      addNotification({ type: 'error', message: 'Failed to copy' });
    }
  };

  // Test API connection
  const testApiConnection = async (config) => {
    setTestingId(config.id);
    setTestResults(prev => ({ ...prev, [config.id]: { status: 'testing', message: 'Testing connection...' } }));

    try {
      const provider = getProvider(config.providerId);

      if (!provider) {
        throw new Error('Unknown provider');
      }

      let url = config.baseUrl || provider.baseUrl;
      let endpoint = config.customEndpoint || provider.testEndpoint;

      // Handle model in URL for some providers
      if (endpoint.includes('{model}')) {
        endpoint = endpoint.replace('{model}', config.model);
      }

      // For Hugging Face, model is part of URL
      if (config.providerId === 'huggingface') {
        url = `${url}/${config.model}`;
      } else {
        url = url + endpoint;
      }

      // For Google, add key to URL
      if (provider.urlWithKey) {
        url = `${url}?key=${config.apiKey}`;
      }

      const headers = provider.authHeader ? provider.authHeader(config.apiKey) : {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`
      };

      // Add custom headers if provided
      if (config.customHeaders) {
        try {
          const customHeaders = JSON.parse(config.customHeaders);
          Object.assign(headers, customHeaders);
        } catch (e) {
          console.warn('Failed to parse custom headers');
        }
      }

      const payload = provider.testPayload ? provider.testPayload(config.model) : {
        prompt: 'Test',
        max_tokens: 10
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setTestResults(prev => ({
          ...prev,
          [config.id]: {
            status: 'success',
            message: 'API connection successful!',
            response: JSON.stringify(data, null, 2).substring(0, 500)
          }
        }));
        addNotification({ type: 'success', message: `${config.name} API test successful!` });
      } else {
        throw new Error(data.error?.message || data.message || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('API Test Error:', error);
      setTestResults(prev => ({
        ...prev,
        [config.id]: {
          status: 'error',
          message: error.message || 'Connection failed'
        }
      }));
      addNotification({ type: 'error', message: `API test failed: ${error.message}` });
    } finally {
      setTestingId(null);
    }
  };

  // Add new configuration
  const handleAddConfig = async () => {
    if (!formData.providerId || !formData.apiKey) {
      addNotification({ type: 'error', message: 'Please select a provider and enter API key' });
      return;
    }

    try {
      const isFirstConfig = apiConfigs.length === 0;
      const result = await aiSettingsService.create({
        ...formData,
        isActive: isFirstConfig, // Set as active if it's the first config
      });

      if (result.success) {
        resetForm();
        addNotification({ type: 'success', message: 'API configuration added successfully!' });
      } else {
        addNotification({ type: 'error', message: result.error || 'Failed to save configuration' });
      }
    } catch (error) {
      console.error('Error adding config:', error);
      addNotification({ type: 'error', message: 'Failed to save configuration' });
    }
  };

  // Update existing configuration
  const handleUpdateConfig = async () => {
    if (!formData.apiKey) {
      addNotification({ type: 'error', message: 'API key is required' });
      return;
    }

    try {
      const result = await aiSettingsService.update(editingId, formData);

      if (result.success) {
        // Clear test results for this config
        setTestResults(prev => {
          const newResults = { ...prev };
          delete newResults[editingId];
          return newResults;
        });

        resetForm();
        addNotification({ type: 'success', message: 'API configuration updated!' });
      } else {
        addNotification({ type: 'error', message: result.error || 'Failed to update configuration' });
      }
    } catch (error) {
      console.error('Error updating config:', error);
      addNotification({ type: 'error', message: 'Failed to update configuration' });
    }
  };

  // Delete configuration
  const handleDeleteConfig = async (configId) => {
    if (window.confirm('Are you sure you want to delete this API configuration?')) {
      try {
        const result = await aiSettingsService.delete(configId);

        if (result.success) {
          addNotification({ type: 'success', message: 'API configuration deleted' });
        } else {
          addNotification({ type: 'error', message: result.error || 'Failed to delete configuration' });
        }
      } catch (error) {
        console.error('Error deleting config:', error);
        addNotification({ type: 'error', message: 'Failed to delete configuration' });
      }
    }
  };

  // Set as active configuration
  const handleSetActive = async (configId) => {
    try {
      const result = await aiSettingsService.setActive(configId);

      if (result.success) {
        setActiveConfig(configId);
        addNotification({ type: 'success', message: 'Active API configuration updated!' });
      } else {
        addNotification({ type: 'error', message: result.error || 'Failed to set active configuration' });
      }
    } catch (error) {
      console.error('Error setting active config:', error);
      addNotification({ type: 'error', message: 'Failed to set active configuration' });
    }
  };

  // Start editing
  const startEditing = (config) => {
    setFormData({
      providerId: config.providerId,
      name: config.name,
      apiKey: config.apiKey,
      baseUrl: config.baseUrl,
      model: config.model,
      isActive: config.isActive,
      customHeaders: config.customHeaders || '',
      customEndpoint: config.customEndpoint || ''
    });
    setEditingId(config.id);
    setIsAddingNew(false);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      providerId: '',
      name: '',
      apiKey: '',
      baseUrl: '',
      model: '',
      isActive: true,
      customHeaders: '',
      customEndpoint: ''
    });
    setIsAddingNew(false);
    setEditingId(null);
  };

  // Toggle API key visibility
  const toggleShowApiKey = (configId) => {
    setShowApiKey(prev => ({ ...prev, [configId]: !prev[configId] }));
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="ai-settings">
        <div className="loading-container">
          <Loader2 size={48} className="spinning" />
          <h3>Loading AI Settings...</h3>
          <p>Fetching API configurations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-settings">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>AI API Settings</h1>
          <p>Manage your AI provider API keys and configurations</p>
        </div>
        <Button
          variant="primary"
          onClick={() => { setIsAddingNew(true); setEditingId(null); }}
          disabled={isAddingNew}
        >
          <Plus size={18} />
          Add AI Provider
        </Button>
      </div>

      {/* Quick Switch Active API */}
      {apiConfigs.length > 0 && (
        <Card className="quick-switch-card">
          <CardContent>
            <div className="quick-switch-content">
              <div className="quick-switch-left">
                <div className="active-indicator-large">
                  <Zap size={20} />
                </div>
                <div className="quick-switch-info">
                  <span className="quick-switch-label">Active AI Provider</span>
                  <span className="quick-switch-hint">Select which API to use for AI features</span>
                </div>
              </div>
              <div className="quick-switch-right">
                <select
                  className="quick-switch-select"
                  value={activeConfig || ''}
                  onChange={(e) => handleSetActive(e.target.value)}
                >
                  <option value="" disabled>Select Active API...</option>
                  {apiConfigs.map(config => (
                    <option key={config.id} value={config.id}>
                      {config.name}
                    </option>
                  ))}
                </select>
                {activeConfig && (
                  <button
                    className="test-active-btn"
                    onClick={() => {
                      const config = apiConfigs.find(c => c.id === activeConfig);
                      if (config) testApiConnection(config);
                    }}
                    disabled={testingId === activeConfig}
                  >
                    {testingId === activeConfig ? (
                      <Loader2 size={16} className="spinning" />
                    ) : (
                      <RefreshCw size={16} />
                    )}
                    Test
                  </button>
                )}
              </div>
            </div>
            {activeConfig && (
              <div className="active-config-details">
                {(() => {
                  const config = apiConfigs.find(c => c.id === activeConfig);
                  const provider = config ? getProvider(config.providerId) : null;
                  const testResult = testResults[activeConfig];
                  return config ? (
                    <>
                      <div className="active-config-row">
                        {provider?.logo && (
                          <img src={provider.logo} alt={config.name} className="provider-logo-medium" />
                        )}
                        <div className="active-config-meta">
                          <span className="active-provider-name">{provider?.name || 'Custom'}</span>
                          <span className="active-model-name">{config.model}</span>
                        </div>
                        <div className="active-config-actions">
                          {testResult && (
                            <div className={`active-status ${testResult.status}`}>
                              {testResult.status === 'success' && <CheckCircle size={16} />}
                              {testResult.status === 'error' && <XCircle size={16} />}
                              {testResult.status === 'testing' && <Loader2 size={16} className="spinning" />}
                              <span>{testResult.status === 'success' ? 'Connected' : testResult.status === 'error' ? 'Failed' : 'Testing...'}</span>
                            </div>
                          )}
                          <button
                            className="config-action-btn edit"
                            onClick={() => startEditing(config)}
                            title="Edit Configuration"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="config-action-btn delete"
                            onClick={() => handleDeleteConfig(config.id)}
                            title="Delete Configuration"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : null;
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Form */}
      {(isAddingNew || editingId) && (
        <Card className="config-form-card">
          <CardContent>
            <div className="form-header">
              <h3>{editingId ? 'Edit API Configuration' : 'Add New AI Provider'}</h3>
              <button className="close-btn" onClick={resetForm}>
                <X size={20} />
              </button>
            </div>

            <div className="config-form">
              {/* Provider Selection */}
              <div className="form-group">
                <label>AI Provider</label>
                <select
                  value={formData.providerId}
                  onChange={(e) => handleInputChange('providerId', e.target.value)}
                  className="select"
                  disabled={!!editingId}
                >
                  <option value="">Select a provider...</option>
                  {AI_PROVIDERS.map(provider => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Display Name */}
              <div className="form-group">
                <label>Display Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Production GPT-4"
                />
              </div>

              {/* API Key */}
              <div className="form-group">
                <label>
                  API Key
                  {formData.providerId && getProvider(formData.providerId)?.docUrl && (
                    <a
                      href={getProvider(formData.providerId).docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="doc-link"
                    >
                      <ExternalLink size={14} />
                      Get API Key
                    </a>
                  )}
                </label>
                <div className="api-key-input">
                  <Input
                    type={showApiKey['form'] ? 'text' : 'password'}
                    value={formData.apiKey}
                    onChange={(e) => handleInputChange('apiKey', e.target.value)}
                    placeholder={`Enter your ${formData.providerId ? getProvider(formData.providerId)?.keyPrefix || '' : ''}API key`}
                    icon={Key}
                  />
                  <button
                    type="button"
                    className="toggle-visibility"
                    onClick={() => toggleShowApiKey('form')}
                  >
                    {showApiKey['form'] ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Model Selection */}
              {formData.providerId && !getProvider(formData.providerId)?.isCustom && (
                <div className="form-group">
                  <label>Model</label>
                  <select
                    value={formData.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    className="select"
                  >
                    {getProvider(formData.providerId)?.models.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Custom Provider Fields */}
              {formData.providerId === 'custom' && (
                <>
                  <div className="form-group">
                    <label>Base URL</label>
                    <Input
                      value={formData.baseUrl}
                      onChange={(e) => handleInputChange('baseUrl', e.target.value)}
                      placeholder="https://api.example.com/v1"
                      icon={Globe}
                    />
                  </div>
                  <div className="form-group">
                    <label>Model Name</label>
                    <Input
                      value={formData.model}
                      onChange={(e) => handleInputChange('model', e.target.value)}
                      placeholder="model-name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Custom Endpoint (optional)</label>
                    <Input
                      value={formData.customEndpoint}
                      onChange={(e) => handleInputChange('customEndpoint', e.target.value)}
                      placeholder="/chat/completions"
                    />
                  </div>
                  <div className="form-group">
                    <label>Custom Headers (JSON, optional)</label>
                    <textarea
                      value={formData.customHeaders}
                      onChange={(e) => handleInputChange('customHeaders', e.target.value)}
                      placeholder='{"X-Custom-Header": "value"}'
                      className="textarea"
                      rows={3}
                    />
                  </div>
                </>
              )}

              {/* Form Actions */}
              <div className="form-actions">
                <Button variant="secondary" onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={editingId ? handleUpdateConfig : handleAddConfig}
                >
                  <Save size={18} />
                  {editingId ? 'Update Configuration' : 'Save Configuration'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="configs-section">
          <Card className="empty-state-card">
            <CardContent>
              <div className="empty-state">
                <Loader2 size={64} className="spinning" />
                <h3>Loading AI Settings...</h3>
                <p>Fetching configurations from Firebase</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Provider Documentation Links */}
      <Card className="docs-section">
        <CardContent>
          <h3>
            <ExternalLink size={20} />
            Quick Links to Get API Keys
          </h3>
          <div className="docs-grid">
            {AI_PROVIDERS.filter(p => !p.isCustom && p.docUrl).map(provider => (
              <a
                key={provider.id}
                href={provider.docUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="doc-card"
              >
                {provider.logo && <img src={provider.logo} alt={provider.name} />}
                <span>{provider.name}</span>
                <ExternalLink size={14} />
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Usage Instructions */}
      <Card className="instructions-card">
        <CardContent>
          <h3>
            <AlertCircle size={20} />
            How to Use
          </h3>
          <div className="instructions">
            <ol>
              <li>
                <strong>Add an API Provider:</strong> Click "Add AI Provider" and select your preferred AI service (OpenAI, Claude, Hugging Face, etc.)
              </li>
              <li>
                <strong>Enter Your API Key:</strong> Get an API key from the provider's website and paste it in the form. Click the documentation links above for help.
              </li>
              <li>
                <strong>Select a Model:</strong> Choose which AI model to use (e.g., GPT-4, Claude-3, etc.)
              </li>
              <li>
                <strong>Test the Connection:</strong> Click "Test API" to verify your API key is working correctly before saving.
              </li>
              <li>
                <strong>Set as Active:</strong> Click "Set Active" on the configuration you want to use for AI features in the system.
              </li>
              <li>
                <strong>Custom APIs:</strong> For open-source or self-hosted APIs (like local LLMs), select "Custom API" and enter your own base URL and endpoint.
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AISettings;
